import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import getRole from '@src/APIs/graphQl/Role/getRole';
import AddRoleButton from '@src/App/Components/Buttons/AddRoleButton/AddRoleButton';
import CertificatesCard from '@src/App/Components/Cards/EntityCards/Certificates/CertificatesCard/CertificatesCard';
import CompetencesCard from '@src/App/Components/Cards/EntityCards/Competences/CompetencesCard/CompetencesCard';
import EmployeesCard from '@src/App/Components/Cards/EntityCards/Employees/EmployeesCard/EmployeesCard';
import ProductsCard from '@src/App/Components/Cards/EntityCards/Products/ProductsCard/ProductsCard';
import SkillsCard from '@src/App/Components/Cards/EntityCards/Skills/SkillsCard/SkillsCard';
import EntityPageInformation, {
    generateAcusitionEntityPageInformation,
} from '@src/App/Components/Core/Layouts/LayoutElements/EntityPageInformation/EntityPageInformation';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import { RoleDialog } from '@src/App/Components/Dialogs/RoleDialog/RoleDialog';
import ExpandableText from '@src/App/Components/General/ExpandableText/ExpandableText';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import { combinedKnowledge } from '@src/helper/helper';
import type Role from '@src/Objects/Role';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import EntityPage from '../../Generic/EntityPage/EntityPage';

interface RolePageProps {
    entity?: Role;
    inDialog?: boolean;
}

const RolePage: React.FC<RolePageProps> = (props) => {
    const { openDialog } = useDialog();
    const navigate = useNavigate();
    const { id } = useParams();
    const entityId = props.entity ? props.entity.id : id ? Number.parseInt(id) : -1;
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).roles;
    const allCompetences = useSelector((state: AppState) => state.company.allCompetences);
    const entityState = useSelector((state: AppState) => state.roles.find((a) => a.id === entityId));

    const emp = useSelector((state: AppState) => state.employee);
    const permissions = useSelector((state: AppState) => state.permissions);

    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);
    const { combinedSkills, combinedCertificates } = combinedKnowledge(entityState, allCompetences);

    useEffect(() => {
        if (props.inDialog) return;
        getRole(entityId).catch((ex) => {
            setLoadingError(ex);
        });
    }, [props.inDialog, entityId]);

    if (!emp) return null;
    const handleOpenEditDialog = async () => {
        return openDialog(<RoleDialog id="RoleDialog" role={entityState} />);
    };
    return (
        <EntityPage
            inDialog={props.inDialog}
            loadingError={loadingError}
            entity={entityState}
            admin={permissions.admin}
            permissions={permissions.entities.Roles}
            onDelete={() => {
                if (!props.inDialog) navigate(`/${ELinks.ROLES}`);
            }}
            buttons={entityState ? <AddRoleButton size="small" id={entityState.id} /> : undefined}
            openEditDialog={handleOpenEditDialog}
            informations={
                entityState
                    ? [
                          entityState.description ? (
                              <EntityPageInformation title={langStrings.description} size={'full'}>
                                  <ExpandableText>
                                      <FormatedTextInput value={entityState.description} readonly={true} />
                                  </ExpandableText>
                              </EntityPageInformation>
                          ) : undefined,
                          //   Aneignung
                          generateAcusitionEntityPageInformation(entityState, langStrings),
                      ]
                    : []
            }
        >
            {entityState && (
                <>
                    <EmployeesCard title={langStrings.employeesWithRole} employees={entityState.employees} />
                    <CompetencesCard competences={entityState.competences} />
                    <SkillsCard title={langStrings.oftenUsed.skills} skills={combinedSkills} />
                    <CertificatesCard
                        certificates={[...combinedCertificates]}
                        title={langStrings.oftenUsed.certificates}
                    />

                    <ProductsCard products={entityState.products} />
                </>
            )}
        </EntityPage>
    );
};

export default RolePage;
